/*
 * Copyright (c) 2021, salesforce.com, inc.
 * SPDX-License-Identifier: BSD-3-Clause
 *
 * Search results page header only (`/search?q=...`). Not used on category PLP.
 */
import React, {useCallback} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Stack,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

import {useTrackSelectItem, VIEW_ITEM_LIST} from '@amblique/king-living/app/analytics/ga4';
import SearchIdlePanel from '@amblique/king-living/app/components/search/partials/search-idle-panel';
import {useContentfulHeader} from '@amblique/king-living/app/hooks/use-contentful-header';
import {
  searchResultsCountTitleTextProps,
  searchResultsEmptyLine1TextProps,
  searchResultsEmptySubtitleTextProps,
} from '@amblique/king-living/app/theme/components/project/search-product-list-page';
import {saveRecentSearch} from '@amblique/king-living/app/utils/search-recent';
import {productUrlBuilder} from '@amblique/king-living/app/utils/url';

const SearchResultsSummary = ({searchQuery, productSearchResult, isLoading, isRefetching}) => {
  const intl = useIntl();
  const [searchParamsState, {stringify}] = useSearchParams();
  const navigate = useNavigation();
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {
    data: contentfulHeader,
    isLoading: isHeaderLoading,
  } = useContentfulHeader(siteId, {
    enabled: Boolean(siteId),
  });

  const searchIdle = contentfulHeader?.searchIdle;
  const trackSelectItem = useTrackSelectItem();

  const total = productSearchResult?.total;
  const summaryPending =
    isLoading ||
    (Boolean(isRefetching) && !(productSearchResult?.hits?.length > 0));

  const showSummary = !summaryPending && total !== undefined;
  const isEmpty = showSummary && total === 0;

  const handlePickPopularSearch = useCallback(
    (term) => {
      const t = String(term || '').trim();

      if (!t) {
        return;
      }

      saveRecentSearch(t);
      const nextParams = {...searchParamsState, q: t};

      navigate(`/search?${stringify(nextParams)}`);
    },
    [navigate, searchParamsState, stringify]
  );

  const handlePickBlog = useCallback(
    (blog, {skipNavigate = false} = {}) => {
      const path = String(blog?.pageSlug || '').trim();

      if (!path) {
        return;
      }

      if (!skipNavigate) {
        navigate(path);
      }
    },
    [navigate]
  );

  const handlePickProduct = useCallback(
    (product, index) => {
      if (typeof index === 'number') {
        void trackSelectItem({
          index,
          listMeta: VIEW_ITEM_LIST.QUICK_SEARCH,
          product,
        });
      }

      const link = productUrlBuilder(product);

      if (!link) {
        return;
      }

      navigate(link);
    },
    [navigate, trackSelectItem]
  );

  const quotedQuery = (() => {
    const t = searchQuery || '';

    if (intl.locale?.toLowerCase().startsWith('fr')) {
      return `\u00AB${t}\u00BB`;
    }

    return `\u2018${t}\u2019`;
  })();

  return (
    <Box width="100%">
      {showSummary ? (
        isEmpty ? (
          <Stack data-testid="search-results-empty-messages" spacing={{base: 4, lg: 4}} textAlign="left">
            <Text {...searchResultsEmptyLine1TextProps}>
              <FormattedMessage
                defaultMessage="Sorry, no matches were found for {quotedQuery}"
                id="search.results.none.line1"
                values={{quotedQuery}}
              />
            </Text>
            <Text {...searchResultsEmptySubtitleTextProps}>
              <FormattedMessage
                defaultMessage="Please try a new search or use suggestions."
                id="search.results.none.line2"
              />
            </Text>
            {isHeaderLoading || searchIdle ? (
              <Box data-testid="search-results-idle-panel" width="100%">
                <SearchIdlePanel
                  isHeaderLoading={isHeaderLoading}
                  searchIdle={searchIdle}
                  variant="searchResults"
                  onPickBlog={handlePickBlog}
                  onPickPopularSearch={handlePickPopularSearch}
                  onPickProduct={handlePickProduct}
                />
              </Box>
            ) : null}
          </Stack>
        ) : (
          <Text
            data-testid="search-results-count-title"
            {...searchResultsCountTitleTextProps}
          >
            <FormattedMessage
              defaultMessage="{count, number} results found for {quotedQuery}"
              id="search.results.found_for"
              values={{
                count: total,
                quotedQuery,
              }}
            />
          </Text>
        )
      ) : (
        <Box minHeight="28px" />
      )}
    </Box>
  );
};

SearchResultsSummary.propTypes = {
  isLoading: PropTypes.bool,
  isRefetching: PropTypes.bool,
  productSearchResult: PropTypes.object,
  searchQuery: PropTypes.string,
};

/**
 * Search PLP header: results summary and idle panel when empty (no inline search field).
 */
const SearchPageHeader = ({
  productSearchResult,
  isLoading,
  isRefetching,
  searchQuery,
  ...otherProps
}) => (
  <Box
    className="sf-product-list-page-header-search"
    data-testid="sf-product-list-page-header-search"
    width="100%"
    {...otherProps}
  >
    <SearchResultsSummary
      isLoading={isLoading}
      isRefetching={isRefetching}
      productSearchResult={productSearchResult}
      searchQuery={searchQuery}
    />
  </Box>
);

SearchPageHeader.propTypes = {
  isLoading: PropTypes.bool,
  isRefetching: PropTypes.bool,
  productSearchResult: PropTypes.object,
  searchQuery: PropTypes.string.isRequired,
};

export default SearchPageHeader;
